import React from 'react';
import LegalPageLayout, { LegalSection } from '../components/LegalPageLayout';

const PrivacyPolicyPage = () => {
  return (
    <LegalPageLayout
      testId="privacy-policy-page"
      eyebrow="Legal"
      title="Privacy Policy"
      lastUpdated="February 2026"
    >
      <LegalSection number="1" title="Who we are">
        <p>
          Sebastian Alliance Group LLC ("SAG", "we", "us") is a Florida
          limited liability company headquartered at 101 N.E. 3rd Avenue,
          Suite 1500, Fort Lauderdale, FL 33301. This policy covers
          information handled by SAG as the operator of our corporate
          websites, Payment Center, Investor Portal, Admin portal and related
          digital products.
        </p>
        <p>
          This is <strong>not</strong> the privacy policy of any merchant,
          client, or portfolio company you may transact with through our
          platforms — those parties publish their own policies.
        </p>
      </LegalSection>

      <LegalSection number="2" title="Information we collect">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Contact and identity data</strong> you provide through
            forms — name, business name, email address, phone number, postal
            address, and any message or file you attach.
          </li>
          <li>
            <strong>Transaction data</strong> — amount, currency, date, time,
            reference/invoice ID, approval code and result of each payment.
          </li>
          <li>
            <strong>Limited payment method data</strong> returned by the
            processor — card brand and the last four digits of the card. We
            never receive or store full card numbers, CVV codes, expiry dates
            or online banking credentials.
          </li>
          <li>
            <strong>KYC/AML data</strong> where required — legal name, date of
            birth, government ID reference, business registration documents
            and beneficial-ownership information collected in accordance with
            our{' '}
            <a href="/kyc-aml" className="text-[#C9A227] hover:underline">
              KYC/AML Policy
            </a>
            .
          </li>
          <li>
            <strong>Technical data</strong> — IP address, device and browser
            information, referring page, server logs and error reports, used
            for security, fraud prevention and reliability.
          </li>
          <li>
            <strong>Account data</strong> — for administrators and
            authenticated users, hashed credentials, role, session tokens and
            audit-log entries of privileged actions.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number="3" title="How we use it">
        <p>
          We use the information to: process and confirm payments; issue
          invoices and receipts; deliver services you requested; prevent and
          investigate fraud, chargebacks and abuse; comply with tax,
          accounting, KYC, AML, sanctions and other legal obligations;
          respond to enquiries and provide support; and improve the security
          and reliability of our platforms.
        </p>
      </LegalSection>

      <LegalSection number="4" title="Who we share it with">
        <p>
          We share information with: our payment processors and banking
          partners (Stripe, Fiserv and their sub-processors) as required to
          complete transactions; service providers acting on our written
          instructions (for example hosting, email delivery, analytics,
          error monitoring); professional advisers (accountants, lawyers,
          auditors); and regulators, law enforcement or courts where legally
          required or to protect our rights.
        </p>
        <p>
          <strong>We do not sell personal information.</strong> We do not
          share personal information for cross-context behavioural
          advertising.
        </p>
      </LegalSection>

      <LegalSection number="5" title="International transfers">
        <p>
          SAG is based in the United States and our service providers may
          process data in the United States, the European Union, the United
          Kingdom and other jurisdictions. Where personal data is transferred
          out of the EEA or UK, we rely on appropriate safeguards such as the
          European Commission's Standard Contractual Clauses.
        </p>
      </LegalSection>

      <LegalSection number="6" title="Retention and security">
        <p>
          Transaction and KYC records are retained for the period required by
          US federal and Florida state tax, accounting, banking and
          anti-money-laundering law — typically at least five (5) years from
          the end of the business relationship, and longer where a specific
          legal or regulatory obligation applies. Other personal data is kept
          only as long as needed for the purpose it was collected.
        </p>
        <p>
          Data is transmitted over HTTPS/TLS. Sensitive configuration such as
          processor API keys is encrypted at rest with authenticated
          encryption. Access is role-based, logged and restricted to
          authorised personnel.
        </p>
      </LegalSection>

      <LegalSection number="7" title="Your rights">
        <p>
          Depending on where you are located, you may have the right to
          access, correct, delete, port or restrict processing of your
          personal information, and to object to certain processing. To
          exercise any of these rights, contact us at{' '}
          <a
            href="mailto:ecommerce@sebasalliance.net"
            className="text-[#C9A227] hover:underline"
          >
            ecommerce@sebasalliance.net
          </a>
          . We will respond within the timeframe required by applicable law.
          We may need to keep records that we are required to retain by law
          (for example transaction and KYC records).
        </p>
        <p>
          <strong>California residents (CCPA/CPRA):</strong> you have the
          right to know what personal information we collect, to request
          deletion or correction, and to opt out of any "sale" or "sharing"
          for cross-context behavioural advertising. We do not sell or share
          your personal information as those terms are defined by the CCPA.
        </p>
        <p>
          <strong>EEA/UK residents (GDPR/UK GDPR):</strong> our lawful bases
          for processing include performance of a contract, compliance with a
          legal obligation, our legitimate interests in operating and
          securing the platform, and where appropriate, consent. You have the
          right to lodge a complaint with your local data protection
          authority.
        </p>
      </LegalSection>

      <LegalSection number="8" title="Cookies and analytics">
        <p>
          We use cookies and local storage that are necessary to operate the
          platform (for example maintaining your admin session or preventing
          cross-site request forgery), and limited product analytics to
          understand aggregated usage and diagnose issues. Analytics are
          disabled on sensitive routes such as the Admin portal and Payment
          Center so that credentials and card entry are never captured or
          replayed.
        </p>
      </LegalSection>

      <LegalSection number="9" title="Children">
        <p>
          Our platforms are not directed to children under 16 and we do not
          knowingly collect personal information from them. If you believe a
          child has provided us information, please contact us and we will
          delete it.
        </p>
      </LegalSection>

      <LegalSection number="10" title="Changes to this policy">
        <p>
          We may update this policy from time to time. The "Last updated"
          date at the top of this page reflects the most recent version.
          Material changes will be highlighted on our website.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default PrivacyPolicyPage;
